import { useState, useEffect } from "react";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "../../../../components/MDBox";
import MDTypography from "../../../../components/MDTypography";
import MDAvatar from "../../../../components/MDAvatar";
import { PUBLIC_URL } from "../../../../utils/axiosConfig";
import profileService from "../../../../services/profileService";
import MDSnackbar from "../../../../components/MDSnackbar";

// Material Dashboard 2 React base styles
import breakpoints from "../../../../assets/theme/base/breakpoints";

// Images
import burceMars from "../../../../assets/images/bruce-mars.jpg";
import backgroundImage from "../../../../assets/images/bg-profile.png";

function Header({ children, user, setSetting }) {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return burceMars;
    if (avatarPath.startsWith("data:image")) return avatarPath;
    if (avatarPath.startsWith("/uploads") || avatarPath.startsWith("uploads")) {
      const path = avatarPath.startsWith("/") ? avatarPath : `/${avatarPath}`;
      return `${PUBLIC_URL}${path}`;
    }
    return `${PUBLIC_URL}/uploads/${avatarPath}`;
  };

  const [avatar, setAvatar] = useState(getAvatarUrl(user.avatar));
  const [notification, setNotification] = useState({
    open: false,
    color: "info",
    title: "",
    content: "",
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      try {
        const response = await profileService.updateAvatar(user.id, { avatar: base64String });
        const updatedAvatar = response.data.data.avatar;
        
        setAvatar(getAvatarUrl(updatedAvatar));
        
        // Update local storage user data
        const updatedUser = { ...user, avatar: updatedAvatar };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        setNotification({
          open: true,
          color: "success",
          title: "Éxito",
          content: "Avatar actualizado correctamente",
        });
      } catch (error) {
        setNotification({
          open: true,
          color: "error",
          title: "Error",
          content: "No se pudo actualizar el avatar",
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSetSetting = (value) => {
    setSetting(value);
  };

  useEffect(() => {
    // A function that sets the orientation state of the tabs.
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    /** 
     The event listener that's calling the handleTabsOrientation function when resizing the window.
    */
    window.addEventListener("resize", handleTabsOrientation);

    // Call the handleTabsOrientation function to set the state with the initial value.
    handleTabsOrientation();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => setTabValue(newValue);

  return (
    <MDBox position="relative" mb={5}>
      <MDBox
        display="flex"
        alignItems="center"
        position="relative"
        minHeight="18.75rem"
        borderRadius="xl"
        sx={{
          backgroundImage: ({
            functions: { rgba, linearGradient },
            palette: { gradients },
          }) =>
            `${linearGradient(
              rgba(gradients.info.main, 0.6),
              rgba(gradients.info.state, 0.6)
            )}, url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "50%",
          overflow: "hidden",
        }}
      />
      <Card
        sx={{
          position: "relative",
          mt: -8,
          mx: 3,
          py: 2,
          px: 2,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <input
              type="file"
              id="avatar-upload"
              style={{ display: "none" }}
              onChange={handleAvatarChange}
              accept="image/*"
            />
            <label htmlFor="avatar-upload">
              <MDAvatar
                src={avatar || burceMars}
                alt="profile-image"
                size="xl"
                shadow="sm"
                sx={{ cursor: "pointer" }}
              />
            </label>
          </Grid>
          <Grid item>
            <MDBox height="100%" mt={0.5} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                {user.username}
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="regular">
                {user.role}
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4} sx={{ ml: "auto" }}>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={tabValue}
                onChange={handleSetTabValue}
              >
                <Tab
                  label="Principal"
                  icon={
                    <Icon fontSize="small" sx={{ mt: -0.25 }}>
                      home
                    </Icon>
                  }
                  onClick={() => handleSetSetting(false)}
                />
                {/* <Tab
                  label="Message"
                  icon={
                    <Icon fontSize="small" sx={{ mt: -0.25 }}>
                      email
                    </Icon>
                  }
                /> */}
                <Tab
                  label="Configuración"
                  icon={
                    <Icon fontSize="small" sx={{ mt: -0.25 }}>
                      settings
                    </Icon>
                  }
                  onClick={() => handleSetSetting(true)}
                />
              </Tabs>
            </AppBar>
          </Grid>
        </Grid>
        {children}
      </Card>
      <MDSnackbar
        color={notification.color}
        icon={notification.color === "error" ? "warning" : "check"}
        title={notification.title}
        content={notification.content}
        dateTime="Ahora"
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
    </MDBox>
  );
}

// Typechecking props for the Header
Header.defaultProps = {
  children: "",
};

Header.propTypes = {
  children: PropTypes.node,
  user: PropTypes.object.isRequired,
  setSetting: PropTypes.func.isRequired,
};

export default Header;
